import React from 'react'
import styled, { ThemeProvider, useTheme } from 'styled-components'

import { Small } from 'components/styled/typography'
import { connect } from 'react-redux'
import { I18nComponentType } from 'i18n/i18n-types'
import { mapLanguageToComponentProps } from 'i18n'
import {
  SmallStyle,
  BodyStyle,
  SubtitleStyle,
} from 'components/styled/typography/styles'
import { VisiblyHidden } from 'theme/accessibility'
import { LoadingIndicator } from 'components/styled/loading-indicator'
import { Box } from 'components/styled/containers/box'
import { RatingStars } from 'components/styled/rating/rating-stars'
import { IconSize, IconSizes } from './star-icon'
import { Icon } from '../icon'
import { RatingsList } from './ratings-list'
import { PopoverTooltip } from '../popover/tooltip'
import { Placement } from '@floating-ui/react-dom-interactions'

export type RatingType = {
  admin: string
  adminName: string
  value: number
}

export interface RatingProps {
  i18n: I18nComponentType
  ownRating?: number
  average?: number
  ratings?: RatingType[]
  onRate?: (rate: number) => void
  onResetRate?: () => void
  size?: IconSize
  isLoading?: boolean
  popOverPlacement?: Placement
  hideReviews?: boolean
}

const TriggerContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`

const StyledRating = styled.div`
  display: flex;
  flex-direction: column;
`

const Ratings = styled.div`
  display: flex;
  align-items: center;
`

export const Average = styled.div<{ size: IconSize }>`
  ${({ size }) => (size === IconSizes.DEFAULT ? SubtitleStyle : BodyStyle)}
  font-weight: 600;
  word-break: normal;
  margin: 0 ${({ theme }) => theme.space.s2} 0 ${({ theme }) => theme.space.s8};
`

const RatingLoadingIndicator = styled(LoadingIndicator)`
  margin: 0;
`

const VisiblyHiddenLabel = styled.span`
  ${VisiblyHidden};
`

export const OwnRating = styled(Small)<{ size: IconSize }>`
  margin-top: ${({ theme }) => theme.space.s4};

  display: flex;
  padding-left: ${({ theme, size }) =>
    size === IconSizes.DEFAULT ? theme.space.s12 : 0};
  justify-content: ${({ size }) =>
    size === IconSizes.DEFAULT || size === IconSizes.LARGE
      ? 'flex-start'
      : 'center'};
  color: ${({ theme }) => theme.color.grey90};
`

const ResetButton = styled.button`
  ${SmallStyle};
  color: ${({ theme }) => theme.color.grey90};

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`

const RatingFC: React.FC<RatingProps> = ({
  i18n,
  ownRating,
  average,
  ratings = [],
  onRate,
  onResetRate,
  size = 'default',
  isLoading = false,
  popOverPlacement = 'bottom',
  hideReviews = false,
  ...props
}) => {
  const showAverage = !!(!hideReviews || ownRating)
  const usedTheme = useTheme()
  const popOverIcon =
    popOverPlacement === 'top' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'

  const ratingsPopover = (
    <div id="ratings-popover" data-testid="ratings-popover">
      <ThemeProvider theme={usedTheme}>
        <RatingsList ratings={ratings} />
      </ThemeProvider>
    </div>
  )

  const arrowTrigger = (
    <TriggerContainer data-testid="arrow-down">
      <Icon type={popOverIcon} />
    </TriggerContainer>
  )

  return (
    <StyledRating {...props}>
      <Ratings>
        <RatingStars
          ownRating={ownRating}
          average={average}
          onRate={onRate}
          size={size}
          isLoading={isLoading}
          showAverage={showAverage}
        />
        {!isLoading && showAverage ? (
          <Average size={size}>
            <VisiblyHiddenLabel>
              {i18n.rating.averageRating}:
            </VisiblyHiddenLabel>
            {(average || 0).toFixed(1)}
          </Average>
        ) : (
          <>
            {isLoading ? (
              <Box ml="s8">
                <RatingLoadingIndicator color="primary30" size="s16" />
              </Box>
            ) : null}
          </>
        )}
        {ratings.length > 0 && (
          <PopoverTooltip
            placement={popOverPlacement}
            interaction="onClick"
            trigger={arrowTrigger}
          >
            {ratingsPopover}
          </PopoverTooltip>
        )}
      </Ratings>
      {!!ownRating && (
        <OwnRating size={size}>
          <span>{`${i18n.rating.ownVote}: ${ownRating.toFixed(1)} `} </span>
          {onResetRate && (
            <ResetButton
              aria-label={i18n.rating.yourRatingReset}
              disabled={isLoading}
              onClick={() => onResetRate()}
            >
              ({i18n.rating.reset})
            </ResetButton>
          )}
        </OwnRating>
      )}
    </StyledRating>
  )
}

export const Rating = connect<{
  i18n: I18nComponentType
}>(mapLanguageToComponentProps())(RatingFC)
